import { Autocomplete, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import {
  ALL,
  buildReviewFilterOptions,
  hasActiveReviewFilters,
} from '../utils/bulkLotNdcReviewFiltering';
import { ndcDigits } from '../utils/bulkOverrideFilters';

// One width for every filter, so the bar reads as one row of matching fields.
const FILTER_MIN_WIDTH = 180;

// Drug and NDC look like the Select filters: label always above the border, "All" when empty.
const TypeToSearchInput = ({ label, params }) => {
  const { InputLabelProps: labelProps, ...inputParams } = params;
  const textFieldProps = {
    ...inputParams,
    label,
    placeholder: 'All',
    InputLabelProps: { ...labelProps, shrink: true },
    sx: { '& input::placeholder': { color: 'text.primary', opacity: 1 } },
  };

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <TextField {...textFieldProps} />;
};

TypeToSearchInput.propTypes = {
  label: PropTypes.string.isRequired,
  params: PropTypes.shape({ InputLabelProps: PropTypes.shape({}) }).isRequired,
};

/**
 * Typing narrows the NDC list on digits only, so "00071-0123" and "000710123" find the same NDC.
 * With no digits typed the whole list stays on offer.
 */
const filterNdcOptionsByDigits = (options, { inputValue }) => {
  const digits = ndcDigits(inputValue);

  return digits ? options.filter((option) => ndcDigits(option.ndc).includes(digits)) : options;
};

/**
 * POM-6010 — the review grid's filter row. POM-6534: one bar for every group of the grid.
 *
 * Every option is derived from the rows in the batch rather than fetched, and every list is A–Z.
 * Drug and NDC are type-to-search because a batch can carry dozens of each; Facility, Partner and
 * Status stay plain selects.
 *
 * `trailingAction` is the row's right-hand end, for the Commit control. It rides here rather than
 * in the header so the badge counting edits sits next to the rows those edits were made in.
 */
const BulkLotNdcReviewFilters = ({
  rows,
  changes,
  filters,
  onChange,
  search,
  onSearchChange,
  onClearAll,
  trailingAction,
}) => {
  const options = buildReviewFilterOptions({ rows, changes, filters });
  const setFilter = (field, value) => onChange({ ...filters, [field]: value ?? ALL });
  const selectedNdcOption =
    filters.ndc === ALL
      ? null
      : (options.ndcs.find((option) => ndcDigits(option.ndc) === ndcDigits(filters.ndc)) ?? null);

  const select = (field, label, values) => (
    <TextField
      select
      size='small'
      label={label}
      value={filters[field]}
      onChange={(event) => setFilter(field, event.target.value)}
      sx={{ minWidth: FILTER_MIN_WIDTH }}
    >
      <MenuItem value={ALL}>All</MenuItem>
      {values.map((value) => (
        <MenuItem key={value} value={value}>
          {value}
        </MenuItem>
      ))}
    </TextField>
  );

  /*
   * gap alone, no `spacing`: Stack's spacing prop injects a margin-left on every child after the
   * first, through a descendant selector that outranks a child's own sx. That silently beat the
   * `ml: 'auto'` that is supposed to push trailingAction to the end of the row.
   */
  return (
    // pt: the bar is now the first thing in the dialog body, which would clip the floating labels.
    <Stack direction='row' sx={{ pt: 1, mb: 1, flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
      <Autocomplete
        size='small'
        options={options.drugs}
        value={filters.drug === ALL ? null : filters.drug}
        onChange={(_event, value) => setFilter('drug', value)}
        renderInput={(params) => <TypeToSearchInput label='Drug' params={params} />}
        sx={{ minWidth: FILTER_MIN_WIDTH }}
      />
      <Autocomplete
        size='small'
        options={options.ndcs}
        value={selectedNdcOption}
        onChange={(_event, option) => setFilter('ndc', option?.ndc)}
        getOptionLabel={(option) => option.ndc}
        isOptionEqualToValue={(option, value) => ndcDigits(option.ndc) === ndcDigits(value.ndc)}
        filterOptions={filterNdcOptionsByDigits}
        renderOption={(props, option) => (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <Box component='li' {...props} key={option.ndc}>
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Box component='span' sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>
                {option.ndc}
              </Box>
              {option.drugName && (
                <Typography variant='caption' color='text.secondary'>
                  {option.drugName}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        renderInput={(params) => <TypeToSearchInput label='NDC' params={params} />}
        sx={{ minWidth: FILTER_MIN_WIDTH }}
      />
      {select('facility', 'Facility', options.facilities)}
      {select('partner', 'Partner', options.partners)}
      {select('status', 'Status', options.statuses)}
      <TextField
        size='small'
        placeholder='Search order id or Rx number'
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
        sx={{ minWidth: 220 }}
      />
      <Button
        variant='text'
        onClick={onClearAll}
        disabled={!hasActiveReviewFilters({ filters, search })}
      >
        Clear all
      </Button>
      {trailingAction}
    </Stack>
  );
};

BulkLotNdcReviewFilters.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  changes: PropTypes.objectOf(PropTypes.shape({})),
  filters: PropTypes.shape({
    drug: PropTypes.string,
    ndc: PropTypes.string,
    facility: PropTypes.string,
    partner: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  search: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onClearAll: PropTypes.func.isRequired,
  trailingAction: PropTypes.node,
};

BulkLotNdcReviewFilters.defaultProps = {
  changes: {},
  trailingAction: null,
};

export default BulkLotNdcReviewFilters;
